import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import lancamentoHorasService from '../../services/lancamentoHoras.service';
import clienteService from '../../services/cliente.service';
import periodoService from '../../services/periodo.service';
import SePode from '../../components/SePode';   // r90
import tarefaService from '../../services/tarefa.service';     // r241
import TarefaModal from '../Tarefas/TarefaModal';              // r241
import DuracaoModal, { AjusteMultiploModal, PASSO_MINUTOS, opcoesDeHora,
  opcoesDeDuracao, ehMultiploDoPasso, duracaoEhMultipla } from '../../components/DuracaoModal';

// ════════════════════════════════════════════════════════════════════════════
// LancamentoModal — refatorado (r51)
// ────────────────────────────────────────────────────────────────────────────
// Diferenças do r50:
//   1. Qtd/Desloc/Total em formato HH:MM (input mascarado)
//   2. Total mais largo, "Desloc." mais curto
//   3. Busca de Projeto sem precisar de Cliente
//   4. Descrição esticável (flex 1, ocupa altura toda)
//   5. Descrição com toggle Modo Texto / Modo Lista (igual TarefaModal)
//   6. Calendário trata sobreposição (ajusta largura, menores pra cima)
//   7. Intervalo via modal próprio (hh:mm), fragmenta visualmente o bloco
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',  cinzaSt: '#f3f4f6',
  verde:   '#10b981',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706',
  roxo:    '#7c3aed', roxoBg:  '#f5f0ff',
  laranja: '#f97316',
};

// Mapeamento negocios.tipo (P/U/S/T) → lancamentos.tipo (P/A/D/S)
const MAP_TIPO_NEG_PRA_LANC = { P: 'P', U: 'A', S: 'D', T: 'S' };

const TIPO_LANC_LABEL = {
  P: 'Projeto',
  S: 'Sustentação',
  A: 'Atendimento Pontual',
  D: 'Service Desk',
};

const inputStyle = {
  width: '100%', height: '34px',
  padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', fontFamily: "'Manrope', sans-serif",
  outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', fontSize: '11px', fontWeight: '600',
  color: COR.muted, marginBottom: '4px',
};
const disabledStyle = {
  backgroundColor: COR.cinzaBg, color: COR.texto, cursor: 'not-allowed',
};

// r56: constantes compartilhadas do calendário (precisam ser acessadas
// pelo container das 2 colunas pra alinhar a justificativa com o fim do
// calendário — item 3 da r56).
const CAL_HORA_INICIO = 6;
const CAL_HORA_FIM    = 24;
const CAL_ALTURA_HORA = 36;
const CAL_ALTURA_TIMELINE = (CAL_HORA_FIM - CAL_HORA_INICIO) * CAL_ALTURA_HORA;  // 648
const CAL_ALTURA_CORPO    = 14 + CAL_ALTURA_TIMELINE + 30;                       // 692
const CAL_ALTURA_HEADER   = 42;                                                  // ~aprox
const CAL_ALTURA_TOTAL    = CAL_ALTURA_CORPO + CAL_ALTURA_HEADER;                // 734

// ════════════════════════════════════════════════════════════════════════════
// Helpers de conversão hh:mm ↔ decimal
// ════════════════════════════════════════════════════════════════════════════
function decimalParaHHMM(n) {
  const v = Math.max(0, parseFloat(n) || 0);
  const hh = Math.floor(v);
  const mm = Math.round((v - hh) * 60);
  // Corrige caso mm dê 60 por arredondamento
  if (mm === 60) return `${String(hh + 1).padStart(2,'0')}:00`;
  return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
}

function hhmmParaDecimal(s) {
  if (!s) return 0;
  const m = String(s).match(/^(\d{1,3}):(\d{2})$/);
  if (!m) return 0;
  return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
}

// ════════════════════════════════════════════════════════════════════════════
// <CampoHHMM> — input mascarado pra horas (hh:mm)
// ────────────────────────────────────────────────────────────────────────────
// Aceita digitação livre e formata só no blur. Aceita digitar "8" → "08:00",
// "1:30" → "01:30", "830" → "08:30".
// ════════════════════════════════════════════════════════════════════════════
function CampoHHMM({ valor, onChange, disabled, readOnly, style, max, aoDigitarForaDaGrade }) {
  const [textoLocal, setTextoLocal] = useState(decimalParaHHMM(valor));
  const refDigitando = useRef(false);

  // Quando valor muda externamente E não estamos digitando, atualiza
  useEffect(() => {
    if (!refDigitando.current) setTextoLocal(decimalParaHHMM(valor));
  }, [valor]);

  const handleChange = (e) => {
    refDigitando.current = true;
    // Aceita apenas dígitos e ":"
    const limpo = e.target.value.replace(/[^\d:]/g, '');
    setTextoLocal(limpo);
  };

  const handleBlur = () => {
    refDigitando.current = false;
    // Tenta parsear o texto digitado em vários formatos
    let novo = 0;
    const t = textoLocal.trim();
    if (t === '') {
      novo = 0;
    } else if (t.includes(':')) {
      const [hh, mm] = t.split(':');
      novo = (parseInt(hh, 10) || 0) + ((parseInt(mm, 10) || 0) / 60);
    } else {
      // Sem ":" — interpreta dependendo da qtd de dígitos
      // 1-2 dígitos = horas; 3-4 = HMM ou HHMM
      if (t.length <= 2) novo = parseInt(t, 10) || 0;
      else {
        const mm = parseInt(t.slice(-2), 10) || 0;
        const hh = parseInt(t.slice(0, -2), 10) || 0;
        novo = hh + mm / 60;
      }
    }
    if (max !== undefined && novo > max) novo = max;
    if (novo < 0) novo = 0;
    // r208: fora da grade de 15min, quem decide é o usuário — o campo mantém
    // o que foi digitado e o modal oferece as duas opções válidas.
    if (aoDigitarForaDaGrade) {
      const opcoes = opcoesDeDuracao(novo);
      if (opcoes.length) aoDigitarForaDaGrade(decimalParaHHMM(novo), opcoes);
    }
    setTextoLocal(decimalParaHHMM(novo));
    onChange(novo);
  };

  return (
    <input
      type="text"
      value={textoLocal}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={readOnly}
      placeholder="00:00"
      style={{
        ...inputStyle,
        textAlign: 'center',
        ...(disabled || readOnly ? disabledStyle : {}),
        ...(style || {}),
      }}
    />
  );
}

// ════════════════════════════════════════════════════════════════════════════
// r241: ticket — identifica a tarefa que originou o lançamento. Mesmo
// desenho do ícone de chamado usado no browse de tarefas.
function IconeTicket({ tamanho = 14 }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6z" />
      <path d="M13 5v14" strokeDasharray="2 2" />
    </svg>
  );
}

// Tag colorida do cliente (mesmo padrão das demais telas)
function TagCliente({ tag, cor }) {
  if (!tag) return null;
  const bgCor = cor || '#003399';
  const hex = bgCor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) || 0;
  const g = parseInt(hex.substr(2, 2), 16) || 0;
  const b = parseInt(hex.substr(4, 2), 16) || 0;
  const corTexto = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1a1a2e' : '#ffffff';
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: '20px',
      fontSize: '10px', fontWeight: '500', backgroundColor: bgCor, color: corTexto,
      letterSpacing: '0.3px', flexShrink: 0,
    }}>{tag}</span>
  );
}

// <Combobox>
// ════════════════════════════════════════════════════════════════════════════
function Combobox({ label, placeholder, onSelecionar, buscar, disabled, renderItem }) {
  const [q, setQ] = useState(label || '');
  const [opcoes, setOpcoes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const refContainer = useRef(null);
  const refDebounce = useRef(null);

  useEffect(() => { setQ(label || ''); }, [label]);

  useEffect(() => {
    const onFora = e => {
      if (refContainer.current && !refContainer.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', onFora);
    return () => document.removeEventListener('mousedown', onFora);
  }, []);

  const handleChange = (texto) => {
    if (disabled) return;
    setQ(texto);
    if (refDebounce.current) clearTimeout(refDebounce.current);
    refDebounce.current = setTimeout(async () => {
      setCarregando(true);
      try {
        const lista = await buscar(texto);
        setOpcoes(lista);
        setAberto(true);
      } catch (err) { console.warn(err); }
      finally { setCarregando(false); }
    }, 200);
  };

  const limpar = () => {
    if (disabled) return;
    setQ('');
    onSelecionar(null);
  };

  return (
    <div ref={refContainer} style={{ position: 'relative' }}>
      <input type="text" value={q}
             onChange={e => handleChange(e.target.value)}
             onFocus={() => { if (!disabled) handleChange(q || ''); }}
             placeholder={placeholder}
             disabled={disabled}
             style={{
               ...inputStyle,
               paddingRight: !disabled && q ? '28px' : '10px',
               ...(disabled ? disabledStyle : {}),
             }} />
      {!disabled && q && (
        <button type="button" onClick={limpar}
          style={{
            position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
            width: '14px', height: '14px', borderRadius: '50%',
            border: 'none', backgroundColor: '#FFCCCC', color: '#990000',
            fontSize: '14px', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', padding: 0, lineHeight: 1,
          }}>×</button>
      )}
      {aberto && !disabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          maxHeight: '220px', overflowY: 'auto',
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100,
        }}>
          {carregando ? (
            <div style={{ padding: '10px', color: COR.muted, fontSize: '12px' }}>Buscando...</div>
          ) : opcoes.length === 0 ? (
            <div style={{ padding: '10px', color: COR.muted, fontSize: '12px', fontStyle: 'italic' }}>
              Nada encontrado.
            </div>
          ) : opcoes.map(op => (
            <div key={op.value} onClick={() => { onSelecionar(op); setAberto(false); }}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                fontSize: '12px', color: COR.texto,
                borderBottom: `1px solid ${COR.borda}`,
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = COR.azulBg}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = COR.branco}>
              {renderItem ? renderItem(op) : op.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <DescricaoChecklist> — clonado do TarefaModal (r51)
// ────────────────────────────────────────────────────────────────────────────
// Modo TEXTO: textarea normal
// Modo LISTA: cada linha "- [ ] item" ou "- [x] item" vira checkbox
// Texto solto entre os checkboxes vira input normal
// Auto-detect ao abrir: se já tem checkboxes, abre em modo lista
// ════════════════════════════════════════════════════════════════════════════
function DescricaoChecklist({ valor, onChange, disabled, placeholder, altura }) {
  const temCheckBox = /^- \[[ x]\] /m.test(valor || '');
  const [modo, setModo] = useState(temCheckBox ? 'lista' : 'texto');

  const alternar = () => setModo(m => m === 'texto' ? 'lista' : 'texto');

  const linhas = (valor || '').split('\n');
  const itensLista = linhas.map((l, i) => {
    const m = l.match(/^- \[([ x])\] (.*)$/);
    return m
      ? { i, tipo: 'check', marcado: m[1] === 'x', texto: m[2] }
      : { i, tipo: 'texto', texto: l };
  });

  const toggleItem = (idx) => {
    if (disabled) return;
    const novasLinhas = linhas.map((l, i) => {
      if (i !== idx) return l;
      const m = l.match(/^- \[([ x])\] (.*)$/);
      if (!m) return l;
      const marcado = m[1] === 'x';
      return `- [${marcado ? ' ' : 'x'}] ${m[2]}`;
    });
    onChange(novasLinhas.join('\n'));
  };

  const editarItem = (idx, novoTexto) => {
    if (disabled) return;
    const novasLinhas = linhas.map((l, i) => {
      if (i !== idx) return l;
      const m = l.match(/^- \[([ x])\] /);
      return m ? `- [${m[1]}] ${novoTexto}` : novoTexto;
    });
    onChange(novasLinhas.join('\n'));
  };

  const removerItem = (idx) => {
    if (disabled) return;
    const novasLinhas = linhas.filter((_, i) => i !== idx);
    onChange(novasLinhas.join('\n'));
  };

  const adicionarItem = () => {
    if (disabled) return;
    const novo = (valor || '').trim();
    const sep = novo ? '\n' : '';
    onChange(novo + sep + '- [ ] ');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '4px',
      }}>
        <span style={labelStyle}>Descrição</span>
        <button type="button" onClick={alternar} disabled={disabled}
          title={modo === 'texto' ? 'Trocar pra modo lista (checkboxes)' : 'Voltar pra modo texto livre'}
          style={{
            padding: '3px 10px', fontSize: '11px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '4px',
            backgroundColor: COR.cinzaBg, color: COR.muted,
            cursor: disabled ? 'default' : 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}>
          {modo === 'texto' ? '☑ Modo Lista' : '☰ Modo Texto'}
        </button>
      </div>

      {modo === 'texto' ? (
        <textarea
          value={valor || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder || 'Descreva o trabalho...'}
          style={{
            width: '100%',
            flex: 1, minHeight: altura || '90px',
            padding: '8px 10px', resize: 'none',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            fontSize: '13px', fontFamily: "'Manrope', sans-serif",
            outline: 'none', boxSizing: 'border-box',
            ...(disabled ? disabledStyle : {}),
          }}
        />
      ) : (
        <div style={{
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          backgroundColor: COR.branco,
          padding: '8px 10px',
          flex: 1, minHeight: altura || '90px',
          overflowY: 'auto',
          fontFamily: "'Manrope', sans-serif",
        }}>
          {itensLista.length === 0 || (itensLista.length === 1 && !itensLista[0].texto) ? (
            <div style={{
              fontSize: '12px', color: COR.muted, fontStyle: 'italic',
              padding: '4px 0',
            }}>
              Lista vazia. Clique em "+ Adicionar item" pra começar.
            </div>
          ) : itensLista.map(item => (
            <div key={item.i} style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              padding: '3px 0', fontSize: '13px',
            }}>
              {item.tipo === 'check' ? (
                <>
                  <input type="checkbox"
                    checked={item.marcado}
                    onChange={() => toggleItem(item.i)}
                    disabled={disabled}
                    style={{
                      marginTop: '4px', cursor: disabled ? 'default' : 'pointer',
                      flexShrink: 0,
                    }} />
                  <input type="text"
                    value={item.texto}
                    onChange={e => editarItem(item.i, e.target.value)}
                    disabled={disabled}
                    style={{
                      flex: 1, border: 'none', outline: 'none',
                      backgroundColor: 'transparent',
                      fontSize: '13px',
                      color: item.marcado ? COR.muted : COR.texto,
                      textDecoration: item.marcado ? 'line-through' : 'none',
                      fontFamily: "'Manrope', sans-serif",
                      padding: '2px 0',
                    }}
                    placeholder="Item da lista..." />
                  {!disabled && (
                    <button type="button" onClick={() => removerItem(item.i)}
                      title="Remover item"
                      style={{
                        border: 'none', background: 'transparent',
                        cursor: 'pointer', color: COR.muted,
                        fontSize: '14px', padding: '0 4px',
                      }}>×</button>
                  )}
                </>
              ) : (
                <input type="text"
                  value={item.texto}
                  onChange={e => editarItem(item.i, e.target.value)}
                  disabled={disabled}
                  style={{
                    flex: 1, border: 'none', outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '13px', color: COR.texto, fontWeight: '500',
                    fontFamily: "'Manrope', sans-serif",
                    padding: '2px 0',
                  }}
                  placeholder="Texto..." />
              )}
            </div>
          ))}

          {!disabled && (
            <button type="button" onClick={adicionarItem}
              style={{
                marginTop: '6px',
                padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                border: `1px dashed ${COR.azul}`, borderRadius: '4px',
                backgroundColor: 'transparent', color: COR.azul,
                cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
              }}>+ Adicionar item</button>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <IntervaloModal> — captura duração HH:MM pra adicionar intervalo
// ════════════════════════════════════════════════════════════════════════════
function IntervaloModal({ horaInicial, onFechar }) {
  const [duracao, setDuracao] = useState(1);  // decimal: 1 = 1h

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, padding: '20px',
      // r133: clique fora NAO fecha — so Cancelar/Confirmar.
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '360px', maxWidth: '100%',
        fontFamily: "'Manrope', sans-serif",
        boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.azul, color: COR.branco,
          fontSize: '13px', fontWeight: '700',
          fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
          borderRadius: '8px 8px 0 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>Adicionar intervalo</span>
          <button onClick={() => onFechar(null)} style={{
            width: '24px', height: '24px', border: 'none',
            backgroundColor: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: '16px' }}>
          {horaInicial && (
            <div style={{ fontSize: '12px', color: COR.muted, marginBottom: '10px' }}>
              Início do intervalo: <strong style={{ color: COR.texto }}>{horaInicial}</strong>
            </div>
          )}
          <label style={labelStyle}>Duração</label>
          <CampoHHMM valor={duracao} onChange={setDuracao} max={23} />
          <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>
            Formato hh:mm (ex: 00:30 = meia hora)
          </div>
        </div>
        <div style={{
          padding: '10px 16px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button onClick={() => onFechar(null)}
            style={{
              padding: '7px 14px', fontSize: '12px', fontWeight: '600',
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.muted, cursor: 'pointer',
            }}>Cancelar</button>
          <button onClick={() => onFechar(duracao)} disabled={duracao <= 0}
            style={{
              padding: '7px 18px', fontSize: '12px', fontWeight: '700',
              border: 'none', borderRadius: '5px',
              backgroundColor: COR.azul, color: COR.branco,
              cursor: duracao <= 0 ? 'not-allowed' : 'pointer',
              opacity: duracao <= 0 ? 0.5 : 1,
            }}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Layout de blocos no calendário — detecta sobreposições e divide largura
// ────────────────────────────────────────────────────────────────────────────
// Recebe array de blocos { id, start, end, groupId?, ... } (start/end decimais)
// Retorna o mesmo array enriquecido com { col, totalCols }
//
// Blocos com mesmo `groupId` são tratados como pertencentes ao mesmo lançamento
// → sempre na mesma coluna. (r52: fragmentos do "meu" lançamento)
//
// Algoritmo: agrupa em "clusters" por sobreposição transitiva.
// Dentro do cluster, blocos MENORES (em duração total do grupo) vão pra esquerda.
// ════════════════════════════════════════════════════════════════════════════
function layoutBlocosSobrepostos(blocos) {
  if (!blocos.length) return [];

  // 1. Agrupa por groupId — cada grupo tem start = mínimo, end = máximo
  const grupos = new Map();
  blocos.forEach(b => {
    const gid = b.groupId || ('_solo_' + b.id);
    if (!grupos.has(gid)) {
      grupos.set(gid, { gid, start: b.start, end: b.end, blocos: [b] });
    } else {
      const g = grupos.get(gid);
      g.start = Math.min(g.start, b.start);
      g.end   = Math.max(g.end, b.end);
      g.blocos.push(b);
    }
  });
  const gruposArr = [...grupos.values()].sort((a, b) => a.start - b.start);

  // 2. Detecta clusters: grupos que se sobrepõem transitivamente
  const clusters = [];
  let cluster = null;
  let clusterEnd = -Infinity;
  gruposArr.forEach(g => {
    if (cluster && g.start < clusterEnd) {
      cluster.push(g);
      clusterEnd = Math.max(clusterEnd, g.end);
    } else {
      if (cluster) clusters.push(cluster);
      cluster = [g];
      clusterEnd = g.end;
    }
  });
  if (cluster) clusters.push(cluster);

  // 3. Aloca colunas dentro de cada cluster (menor duração primeiro)
  const resultado = [];
  clusters.forEach(cl => {
    const cls = [...cl].sort((a, b) => {
      const durA = a.end - a.start;
      const durB = b.end - b.start;
      if (durA !== durB) return durA - durB;
      return a.start - b.start;
    });
    const totalCols = cls.length;
    cls.forEach((g, i) => {
      // Todos os blocos do grupo recebem a mesma coluna
      g.blocos.forEach(b => {
        resultado.push({ ...b, col: i, totalCols });
      });
    });
  });

  return resultado;
}

// ════════════════════════════════════════════════════════════════════════════
// <CalendarioLateral>
// ════════════════════════════════════════════════════════════════════════════
function CalendarioLateral({
  colaboradorId, data, ignorarId,
  meuLancamento,  // { hora_inicio, qtd_horas, intervalos: [{ inicio, duracao }] }
  onAbrirIntervaloModal,
}) {
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState({ lancamentos: [], alocacoes: [] });

  // r56: constantes vêm do escopo do módulo (compartilhadas)
  const HORA_INICIO = CAL_HORA_INICIO;
  const HORA_FIM    = CAL_HORA_FIM;
  const ALTURA_HORA = CAL_ALTURA_HORA;
  const ALTURA_TOTAL = CAL_ALTURA_TIMELINE;
  const PAD_ESQ    = 42;
  const PAD_DIR    = 6;

  const carregar = useCallback(async () => {
    // r209: com a data em branco não há dia pra carregar — encerra o estado de
    // carregamento, senão o painel fica em "Carregando..." até escolherem a data.
    if (!colaboradorId || !data) {
      setDados({ lancamentos: [], alocacoes: [] });
      setCarregando(false);
      return;
    }
    try {
      setCarregando(true);
      const r = await lancamentoHorasService.calendarioDoDia(colaboradorId, data, ignorarId);
      setDados(r);
    } catch (err) {
      console.warn('Falha ao carregar calendário:', err.message);
    } finally {
      setCarregando(false);
    }
  }, [colaboradorId, data, ignorarId]);

  useEffect(() => { carregar(); }, [carregar]);

  const horaParaY = (horaDec) => (horaDec - HORA_INICIO) * ALTURA_HORA;
  const horaStrParaDec = (horaStr) => {
    if (!horaStr) return 0;
    const [h, m] = String(horaStr).split(':').map(Number);
    return (h || 0) + (m || 0) / 60;
  };

  // Linhas horárias
  const linhas = [];
  for (let h = HORA_INICIO; h <= HORA_FIM; h++) {
    linhas.push(
      <div key={h} style={{
        position: 'absolute',
        top: (h - HORA_INICIO) * ALTURA_HORA, left: 0, right: 0,
        borderTop: `1px solid ${COR.borda}`,
      }}>
        <span style={{
          position: 'absolute', left: '4px', top: '-7px',
          fontSize: '10px', color: COR.muted,
          backgroundColor: COR.branco, padding: '0 3px',
        }}>
          {String(h).padStart(2, '0')}:00
        </span>
      </div>
    );
  }

  // ── Junta TODOS os blocos (outros lançamentos + alocações + fragmentos meu) ──
  const todosBlocos = [];

  dados.lancamentos.forEach(l => {
    const start = horaStrParaDec(l.hora_inicio);
    const dur = (parseFloat(l.qtd_horas) || 0) + (parseFloat(l.intervalo) || 0);
    todosBlocos.push({
      id: 'l' + l.id, kind: 'outro_lancamento',
      start, end: start + dur,
      data: l,
    });
  });

  dados.alocacoes.forEach(a => {
    const dInicio = new Date(a.data_inicio);
    const start = dInicio.getHours() + dInicio.getMinutes() / 60;
    const dur = parseFloat(a.horas) || 0;
    todosBlocos.push({
      id: 'a' + a.id, kind: 'alocacao',
      start, end: start + dur,
      data: a,
    });
  });

  // Fragmentos do MEU lançamento (em edição)
  // Se tem intervalos, divide o tempo trabalhado em segmentos
  let fragmentosMeu = [];
  if (meuLancamento.hora_inicio && meuLancamento.qtd_horas > 0) {
    const startMeu = horaStrParaDec(meuLancamento.hora_inicio);
    const intervalos = (meuLancamento.intervalos || [])
      .slice()
      .sort((a, b) => a.inicio - b.inicio);

    if (intervalos.length === 0) {
      // Bloco único
      fragmentosMeu.push({
        start: startMeu, end: startMeu + meuLancamento.qtd_horas,
        ehInicio: true, ehFim: true,
      });
    } else {
      // Cria segmentos entre intervalos. Cada segmento "consome" qtd_horas.
      let restoTrab = meuLancamento.qtd_horas;
      let cursor = startMeu;
      intervalos.forEach((iv, idx) => {
        if (iv.inicio > cursor && restoTrab > 0) {
          const segDur = Math.min(iv.inicio - cursor, restoTrab);
          fragmentosMeu.push({
            start: cursor, end: cursor + segDur,
            ehInicio: idx === 0, ehFim: false,
          });
          restoTrab -= segDur;
        }
        cursor = iv.inicio + iv.duracao;
      });
      if (restoTrab > 0) {
        fragmentosMeu.push({
          start: cursor, end: cursor + restoTrab,
          ehInicio: fragmentosMeu.length === 0, ehFim: true,
        });
      }
    }

    fragmentosMeu.forEach((f, idx) => {
      todosBlocos.push({
        id: 'meu_' + idx, kind: 'meu',
        groupId: 'meu_lanc',  // r52: mesmo groupId = mesma coluna
        start: f.start, end: f.end,
        ehInicio: f.ehInicio, ehFim: f.ehFim,
      });
    });
  }

  // Aplica layout de sobreposições
  const blocosComLayout = layoutBlocosSobrepostos(todosBlocos);

  // Renderiza
  const blocosRender = blocosComLayout.map(b => {
    const top = horaParaY(b.start);
    const altura = (b.end - b.start) * ALTURA_HORA;

    // Largura disponível pra todo o cluster
    // Posicionamento: left + width baseado em col / totalCols
    const larguraDisponivel = `calc(100% - ${PAD_ESQ + PAD_DIR}px)`;
    const widthExpr = `calc((100% - ${PAD_ESQ + PAD_DIR}px) / ${b.totalCols})`;
    const leftExpr  = `calc(${PAD_ESQ}px + ((100% - ${PAD_ESQ + PAD_DIR}px) / ${b.totalCols}) * ${b.col})`;

    if (b.kind === 'outro_lancamento') {
      const l = b.data;
      return (
        <div key={b.id} style={{
          position: 'absolute', top,
          left: leftExpr, width: widthExpr,
          height: Math.max(altura, 14),
          backgroundColor: '#d4f4dd', border: `1px solid ${COR.verde}`,
          borderRadius: '3px', padding: '3px 6px',
          fontSize: '10px', color: COR.texto,
          overflow: 'hidden',
          zIndex: 2,
        }} title={`${l.cliente_fantasia || ''} · ${l.projeto_id || ''}${l.projeto_descricao ? ' — ' + l.projeto_descricao : ''}${l.chamado ? ' · ' + l.chamado : ''}`}>
          <div style={{ fontWeight: '600' }}>{l.cliente_fantasia || l.cliente_nome || '—'}</div>
          {l.projeto_id && (
            <div style={{ fontSize: '9px' }}>
              {l.projeto_id}
              {/* r57 item 5: inclui descrição do projeto */}
              {l.projeto_descricao && <span> — {l.projeto_descricao}</span>}
            </div>
          )}
          {l.chamado && <div style={{ color: COR.muted, fontSize: '9px' }}>{l.chamado}</div>}
        </div>
      );
    }

    if (b.kind === 'alocacao') {
      const a = b.data;
      return (
        <div key={b.id} style={{
          position: 'absolute', top,
          left: leftExpr, width: widthExpr,
          height: Math.max(altura, 14),
          backgroundColor: COR.roxoBg, border: `1px solid ${COR.roxo}`,
          borderRadius: '3px', padding: '3px 6px',
          fontSize: '10px', color: COR.roxo,
          overflow: 'hidden',
          zIndex: 1,
        }} title={a.observacao || 'Alocação'}>
          <div style={{ fontWeight: '600' }}>📌 Alocação</div>
          {a.observacao && <div style={{ fontSize: '9px' }}>{(a.observacao || '').slice(0, 30)}</div>}
        </div>
      );
    }

    // b.kind === 'meu' (lançamento em edição, possivelmente fragmentado)
    return (
      <div key={b.id} style={{
        position: 'absolute', top,
        left: leftExpr, width: widthExpr,
        height: Math.max(altura, 14),
        backgroundColor: COR.azulBg,
        border: `2px solid ${COR.azul}`,
        borderRadius: '3px', padding: '3px 6px',
        fontSize: '10px', color: COR.azul,
        overflow: 'hidden',
        zIndex: 3,
        cursor: 'context-menu',
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const yRel = e.clientY - rect.top;
        // Calcula a hora dentro desse fragmento
        const horaClick = b.start + (yRel / ALTURA_HORA);
        const hh = Math.floor(horaClick);
        const mm = Math.round((horaClick - hh) * 60 / 15) * 15;  // arredonda 15min
        const horaStr = `${String(hh).padStart(2,'0')}:${String(mm % 60).padStart(2,'0')}`;
        onAbrirIntervaloModal(horaClick, horaStr);
      }}
      title="Botão direito: adicionar intervalo">
        {/* r52: replica descrição em todos os fragmentos (não mais "continuação")
            r57 item 5: inclui descrição do projeto */}
        <div style={{ fontWeight: '700' }}>{meuLancamento.cliente_label || 'Em edição'}</div>
        {meuLancamento.projeto_id && (
          <div style={{ fontSize: '10px' }}>
            {meuLancamento.projeto_id}
            {meuLancamento.projeto_descricao && <span> — {meuLancamento.projeto_descricao}</span>}
          </div>
        )}
        {meuLancamento.chamado && <div style={{ color: COR.muted, fontSize: '10px' }}>{meuLancamento.chamado}</div>}
      </div>
    );
  });

  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        backgroundColor: COR.azul, color: COR.branco,
        padding: '10px 14px',
        fontSize: '16px', fontWeight: '700',
        borderRadius: '6px 6px 0 0',
        textAlign: 'center',
        fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      }}>
        {/* SVG: calendário */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        Calendário
      </div>
      {/* r55: container sem scroll — calendário sempre cabe inteiro.
          Altura = label inicial (14px padding) + timeline (ALTURA_TOTAL) + folga (30px). */}
      <div style={{
        height: 14 + ALTURA_TOTAL + 30,
        flexShrink: 0,
        overflow: 'hidden', position: 'relative',
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`,
        borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        paddingTop: '14px',
      }}>
        <div style={{ position: 'relative', height: ALTURA_TOTAL + 30 }}>
          {linhas}
          {blocosRender}
        </div>
        {carregando && (
          <div style={{
            position: 'absolute', top: 4, right: 4,
            fontSize: '9px', color: COR.muted, fontStyle: 'italic',
          }}>...</div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LancamentoModal principal
// ════════════════════════════════════════════════════════════════════════════
// r209: `copiaDe` traz um lançamento existente como base pra um novo — todos
// os campos vêm preenchidos, menos data e hora de início, que são justamente
// o que muda de um dia pro outro.
// `preenchido` (novo lançamento aberto de outra tela, ex.: Eventos do negócio)
// traz cliente, projeto, tipo (de negocios.tipo) e chamado já preenchidos; o
// analista continua sendo o colaborador do usuário logado.
function LancamentoModal({ lancamento, copiaDe, preenchido, onFechar }) {
  const { usuario } = useAuth();
  const ehNovo = !lancamento?.id;
  const ehAdmin = (usuario?.perfil || '').toLowerCase() === 'administrador';

  const [form, setForm] = useState({
    colaborador_id:    '',
    colaborador_label: '',
    // r209: data e hora de início abrem VAZIAS — quem lança informa as duas.
    data:              '',
    hora_inicio:       '',
    qtd_horas:         0,          // r208: abre zerada — a duração é informada
    intervalo:         0,          // SOMA (vai pro banco)
    deslocamento:      0,
    cliente_id:        '',
    cliente_label:     '',
    projeto_id:        '',
    projeto_label:     '',
    chamado:           '',
    tipo:              '',
    movimento:         'L',
    status:            'E',        // r55: 'E' Em Análise / 'A' Aprovado / 'R' Retrabalho
    descricao:         '',
    justificativa:     '',
  });

  // r51: lista local de intervalos (NÃO vai pro banco — só uso visual).
  // Formato: [{ inicio: decimal, duracao: decimal }]
  const [intervalosLocais, setIntervalosLocais] = useState([]);

  // Modal de intervalo (controlado)
  const [modalIntervalo, setModalIntervalo] = useState(null);  // { horaClickDec, horaStr }

  // r207: tela de Definir Duração (a mesma do apontamento de tarefas).
  const [duracaoAberta, setDuracaoAberta] = useState(false);
  // r208: { campo, valor, opcoes } enquanto o usuário escolhe entre as duas
  // possibilidades válidas do que digitou fora da grade de 15min.
  const [ajuste, setAjuste] = useState(null);
  // Guarda o início pra qual a duração já foi oferecida, pra a tela não
  // reaparecer toda vez que o foco passa pelo campo sem nada mudar.
  const inicioOferecido = useRef(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [statusPeriodo, setStatusPeriodo] = useState(null);

  // r241: tarefa que originou o lançamento (só quando veio de apontamento).
  // tarefaId vem do listar; o objeto completo só é buscado ao clicar no botão.
  const tarefaId = lancamento?.tarefa_id || null;
  const [tarefaVinculada, setTarefaVinculada] = useState(null);
  const [carregandoTarefa, setCarregandoTarefa] = useState(false);

  const abrirTarefaVinculada = async () => {
    if (!tarefaId || carregandoTarefa) return;
    setErro('');
    setCarregandoTarefa(true);
    try {
      // via_lancamento: o backend confere o vínculo e libera a leitura mesmo
      // pra quem não é solicitante, responsável ou citado na tarefa.
      const t = await tarefaService.buscarPorId(tarefaId, usuario?.id, {
        via_lancamento: lancamento.id,
      });
      setTarefaVinculada(t);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao abrir a tarefa vinculada.');
    } finally {
      setCarregandoTarefa(false);
    }
  };

  const recarregarTarefaVinculada = async () => {
    if (!tarefaId) return;
    try {
      setTarefaVinculada(await tarefaService.buscarPorId(tarefaId, usuario?.id, {
        via_lancamento: lancamento.id,
      }));
    } catch (err) { console.warn('Erro ao recarregar tarefa vinculada:', err); }
  };

  // Inicializa form
  useEffect(() => {
    // r207: marca o início que já veio preenchido como "já oferecido", pra a
    // tela de Duração só aparecer quando o usuário de fato mudar o horário.
    const horaIni = (lancamento?.hora_inicio || '').slice(0, 5);
    const dataIni = lancamento?.id ? (lancamento.data || '').slice(0, 10) : '';
    inicioOferecido.current = `${dataIni}T${horaIni}`;

    if (lancamento?.id) {
      setForm({
        colaborador_id:    lancamento.colaborador_id,
        colaborador_label: lancamento.colaborador_nome || lancamento.colaborador_id,
        data:              (lancamento.data || '').slice(0, 10),
        hora_inicio:       (lancamento.hora_inicio || '09:00').slice(0, 5),
        qtd_horas:         parseFloat(lancamento.qtd_horas || 0),
        intervalo:         parseFloat(lancamento.intervalo || 0),
        deslocamento:      parseFloat(lancamento.deslocamento || 0),
        cliente_id:        lancamento.cliente_id || '',
        cliente_label:     lancamento.cliente_fantasia || lancamento.cliente_nome || '',
        projeto_id:        lancamento.projeto_id || '',
        projeto_label:     lancamento.projeto_id
                              ? `📂 ${lancamento.projeto_id}${lancamento.projeto_descricao ? ' — ' + lancamento.projeto_descricao : ''}`
                              : '',
        chamado:           lancamento.chamado || '',
        tipo:              lancamento.tipo || '',
        movimento:         lancamento.movimento || 'L',
        status:            lancamento.status || 'E',
        descricao:         lancamento.descricao || '',
        justificativa:     lancamento.justificativa || '',
      });
      // Se está editando e tem intervalo, cria 1 intervalo padrão "no meio"
      // só pra visualização. Não temos mais detalhes salvos.
      if (parseFloat(lancamento.intervalo || 0) > 0) {
        const horaIniStr = (lancamento.hora_inicio || '09:00').slice(0, 5);
        const [hh, mm] = horaIniStr.split(':').map(Number);
        const startH = (hh || 0) + ((mm || 0) / 60);
        const qtd = parseFloat(lancamento.qtd_horas || 0);
        const interv = parseFloat(lancamento.intervalo || 0);
        setIntervalosLocais([{
          inicio: startH + qtd / 2,
          duracao: interv,
        }]);
      } else {
        setIntervalosLocais([]);
      }
    } else if (copiaDe) {
      // r209: cópia — tudo do lançamento de origem, menos id, data e início.
      setForm({
        colaborador_id:    copiaDe.colaborador_id || '',
        colaborador_label: copiaDe.colaborador_nome || copiaDe.colaborador_id || '',
        data:              '',
        hora_inicio:       '',
        qtd_horas:         parseFloat(copiaDe.qtd_horas || 0),
        intervalo:         parseFloat(copiaDe.intervalo || 0),
        deslocamento:      parseFloat(copiaDe.deslocamento || 0),
        cliente_id:        copiaDe.cliente_id || '',
        cliente_label:     copiaDe.cliente_fantasia || copiaDe.cliente_nome || '',
        projeto_id:        copiaDe.projeto_id || '',
        projeto_label:     copiaDe.projeto_id
                              ? `📂 ${copiaDe.projeto_id}${copiaDe.projeto_descricao ? ' — ' + copiaDe.projeto_descricao : ''}`
                              : '',
        chamado:           copiaDe.chamado || '',
        tipo:              copiaDe.tipo || '',
        movimento:         copiaDe.movimento || 'L',
        status:            copiaDe.status || 'E',
        descricao:         copiaDe.descricao || '',
        justificativa:     copiaDe.justificativa || '',
      });
      // O intervalo vai como valor (conta no total), mas sem bloco desenhado:
      // a posição dele dependia do horário de início, que não foi copiado.
      setIntervalosLocais([]);
    } else {
      if (preenchido) {
        let chamado = String(preenchido.chamado || '').trim();
        if (chamado && !chamado.startsWith('#')) chamado = '#' + chamado;
        const tipoLanc = MAP_TIPO_NEG_PRA_LANC[preenchido.tipo_negocio] || '';
        setForm(f => ({
          ...f,
          cliente_id:    preenchido.cliente_id || '',
          cliente_label: preenchido.cliente_label || '',
          projeto_id:    preenchido.projeto_id || '',
          projeto_label: preenchido.projeto_id
                           ? `📂 ${preenchido.projeto_id}${preenchido.projeto_descricao ? ' — ' + preenchido.projeto_descricao : ''}`
                           : '',
          tipo:          tipoLanc,
          chamado,
        }));
        // Confirma tipo/cliente pela mesma consulta usada ao escolher o projeto.
        if (preenchido.projeto_id) {
          lancamentoHorasService.projetoLookup(preenchido.projeto_id)
            .then(det => setForm(f => ({
              ...f,
              tipo: MAP_TIPO_NEG_PRA_LANC[det?.tipo] || f.tipo,
              ...(det?.cliente_id && !f.cliente_id ? {
                cliente_id: det.cliente_id, cliente_label: det.cliente_label,
              } : {}),
            })))
            .catch(err => console.warn('Falha ao buscar tipo/cliente do projeto:', err.message));
        }
      }
      preencherMeuColaborador();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lancamento, copiaDe, preenchido]);

  const preencherMeuColaborador = async () => {
    try {
      const lista = await lancamentoHorasService.buscarColaboradores('');
      const nomeUser = (usuario?.nomeAmigavel || '').toLowerCase().trim();
      const meu = lista.find(c =>
        (c.nome_amigavel || '').toLowerCase() === nomeUser ||
        (c.nome || '').toLowerCase() === nomeUser
      );
      if (meu) {
        setForm(f => ({
          ...f,
          colaborador_id: meu.id,
          colaborador_label: meu.nome_amigavel || meu.nome,
        }));
      }
    } catch (err) { console.warn(err); }
  };

  // Status do período
  useEffect(() => {
    if (!form.data) return;
    (async () => {
      try {
        const p = await periodoService.daData(form.data);
        setStatusPeriodo(p?.status || null);
      } catch (err) {
        setStatusPeriodo(null);
      }
    })();
  }, [form.data]);

  const periodoFechado = statusPeriodo === 'F';

  const setCampo = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Comboboxes ───────────────────────────────────────────────────────────
  const buscarColab = async (q) => {
    const lista = await lancamentoHorasService.buscarColaboradores(q);
    return lista.map(c => ({
      value: c.id, label: c.nome_amigavel || c.nome, raw: c,
    }));
  };

  const buscarClientes = async (q) => {
    // r167b: ANTES chamava clienteService.listar() → GET /clientes, que exige
    // permissão de Clientes (clientes:visualizar). Quem lança horas sem acesso
    // ao cadastro de Clientes tomava 403 aqui. Agora usa a referência leve,
    // liberada a QUALQUER autenticado, e o filtro por texto virou server-side (?q=).
    const lista = await clienteService.listarReferencia(q);
    return (Array.isArray(lista) ? lista : [])
      .slice(0, 30)
      .map(c => ({
        value: c.id, label: c.nome_fantasia || c.razao_social || c.id, raw: c,
      }));
  };

  // r51: Busca projetos. Se cliente está selecionado, restringe; senão busca em todos.
  const buscarProjetos = async (q) => {
    const lista = await lancamentoHorasService.projetosDoCliente(form.cliente_id || '', q);
    return (Array.isArray(lista) ? lista : []).map(p => ({
      value: p.id,
      label: `📂 ${p.id}${p.descricao ? ' — ' + p.descricao : ''}`,
      raw: p,
    }));
  };

  const aoSelecionarProjeto = async (op) => {
    if (!op) {
      return setForm(f => ({ ...f, projeto_id: '', projeto_label: '', tipo: '' }));
    }
    setForm(f => ({ ...f, projeto_id: op.value, projeto_label: op.label }));
    try {
      const det = await lancamentoHorasService.projetoLookup(op.value);
      const tipoLanc = MAP_TIPO_NEG_PRA_LANC[det.tipo] || '';
      setForm(f => {
        const novo = {
          ...f,
          tipo: tipoLanc,
          ...(det.cliente_id && (!f.cliente_id || f.cliente_id !== det.cliente_id) ? {
            cliente_id: det.cliente_id,
            cliente_label: det.cliente_label,
          } : {}),
        };
        // r52: pra não-admin + tipo='P' (Projeto), força status='E' (Em Análise)
        if (!ehAdmin && tipoLanc === 'P') {
          novo.status = 'E';
        }
        return novo;
      });
    } catch (err) {
      console.warn('Falha ao buscar tipo/cliente do projeto:', err.message);
    }
  };

  const aoSelecionarCliente = (op) => {
    if (!op) {
      return setForm(f => ({ ...f, cliente_id: '', cliente_label: '' }));
    }
    setForm(f => {
      const novo = { ...f, cliente_id: op.value, cliente_label: op.label };
      if (f.projeto_id && f.cliente_id && f.cliente_id !== op.value) {
        novo.projeto_id = '';
        novo.projeto_label = '';
        novo.tipo = '';
      }
      return novo;
    });
  };

  const validacao24h = () => {
    const [hh, mm] = String(form.hora_inicio || '0:0').split(':').map(Number);
    const inicioH = (hh || 0) + ((mm || 0) / 60);
    const total = inicioH +
      (parseFloat(form.qtd_horas) || 0) +
      (parseFloat(form.intervalo) || 0) +
      (parseFloat(form.deslocamento) || 0);
    return {
      ok: total <= 24,
      disponivel: Math.max(0, 24 - inicioH),
    };
  };

  // ── Adicionar intervalo (via modal) ──────────────────────────────────────
  const aoConfirmarIntervalo = (duracao) => {
    if (!modalIntervalo || !duracao) {
      setModalIntervalo(null);
      return;
    }
    const { horaClickDec } = modalIntervalo;

    // Verifica se cabe no dia
    const [hh, mm] = String(form.hora_inicio || '0:0').split(':').map(Number);
    const inicioH = (hh || 0) + ((mm || 0) / 60);
    const totalApos = inicioH +
      (parseFloat(form.qtd_horas) || 0) +
      (parseFloat(form.intervalo) || 0) +
      duracao +
      (parseFloat(form.deslocamento) || 0);
    if (totalApos > 24) {
      alert(`Não é possível adicionar ${decimalParaHHMM(duracao)} de intervalo: o lançamento ultrapassaria as 24h. Reduza a quantidade de horas trabalhadas.`);
      setModalIntervalo(null);
      return;
    }

    // Adiciona à lista local (com o hora clicada)
    setIntervalosLocais(arr => [...arr, { inicio: horaClickDec, duracao }]);
    // Soma no campo intervalo
    setForm(f => ({ ...f, intervalo: (parseFloat(f.intervalo) || 0) + duracao }));
    setModalIntervalo(null);
  };

  // ── r207: tela de Definir Duração (a mesma do apontamento de tarefas) ────
  // Abre ao sair do Início com hora preenchida e diferente da última oferecida
  // — mesmo gatilho usado nas tarefas, pra não pular na cara a cada foco.
  const aoSairDoInicio = (horaAtual) => {
    if (periodoFechado) return;
    if (!form.data || !horaAtual) return;
    const chave = `${form.data}T${horaAtual}`;
    if (inicioOferecido.current === chave) return;
    inicioOferecido.current = chave;
    setDuracaoAberta(true);
  };

  const aplicarDuracao = (minutos) => {
    if (minutos > 0) setCampo('qtd_horas', minutos / 60);
    setDuracaoAberta(false);
  };

  // ── Salvar ───────────────────────────────────────────────────────────────
  const salvar = async () => {
    setErro('');
    if (periodoFechado) return setErro('Este período está fechado. Não é possível lançar.');
    if (!form.colaborador_id) return setErro('Analista é obrigatório.');
    if (!form.data)           return setErro('Data é obrigatória.');
    if (!form.hora_inicio)    return setErro('Hora de início é obrigatória.');
    // r207: mesma regra do apontamento de tarefas — tudo em múltiplos de 15min.
    // r208: se o usuário recusou o ajuste na hora, é aqui que a gente segura.
    if (!ehMultiploDoPasso(form.hora_inicio)) {
      return setErro(`Hora de início (${form.hora_inicio}) fora dos ${PASSO_MINUTOS} minutos — use 00, 15, 30 ou 45.`);
    }
    if (!duracaoEhMultipla(form.qtd_horas)) {
      return setErro(`Qtd. de horas (${decimalParaHHMM(form.qtd_horas)}) fora dos ${PASSO_MINUTOS} minutos — use 00:15, 00:30, 00:45, 01:00...`);
    }
    if (!form.cliente_id)     return setErro('Cliente é obrigatório.');
    if (!form.projeto_id)     return setErro('Projeto é obrigatório.');

    const v = validacao24h();
    if (!v.ok) {
      return setErro(`Horas excedem o dia (24h). Disponível a partir de ${form.hora_inicio}: ${decimalParaHHMM(v.disponivel)}.`);
    }

    const payload = {
      colaborador_id: form.colaborador_id,
      data:           form.data,
      hora_inicio:    form.hora_inicio,
      qtd_horas:      parseFloat(form.qtd_horas) || 0,
      intervalo:      parseFloat(form.intervalo) || 0,
      deslocamento:   parseFloat(form.deslocamento) || 0,
      cliente_id:     form.cliente_id,
      projeto_id:     form.projeto_id,
      chamado:        form.chamado || null,
      tipo:           form.tipo,
      movimento:      form.movimento,
      ...(ehNovo ? {} : { situacao: lancamento.situacao }),
      status:         form.status,
      descricao:      form.descricao || null,
      justificativa:  form.justificativa || null,
      [ehNovo ? 'criado_por' : 'alterado_por']: usuario?.id,
    };

    try {
      setSalvando(true);
      if (ehNovo) {
        await lancamentoHorasService.criar(payload);
      } else {
        await lancamentoHorasService.alterar(lancamento.id, payload);
      }
      onFechar(true);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const total = (parseFloat(form.qtd_horas) || 0) + (parseFloat(form.deslocamento) || 0);

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
      // r133: clique fora NAO fecha — so os botoes Cancelar/x ou Salvar.
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        width: '1300px', maxWidth: '100%',
        // r57 item 3: modal cresce até o conteúdo natural, sem espaço vazio.
        // maxHeight 92vh garante que em telas pequenas ele rola.
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
        boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.azul, color: COR.branco,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderRadius: '8px 8px 0 0',
          flexShrink: 0,
        }}>
          <h2 style={{
            margin: 0, fontSize: '18px', fontWeight: '700',
            fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            {/* SVG: relógio */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <polyline points="12 7 12 12 15 14"/>
            </svg>
            {!ehNovo
              ? `Editar Lançamento #${lancamento.id}`
              : copiaDe
                ? `Lançamento de Horas — cópia do #${copiaDe.id}`
                : 'Lançamento de Horas'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* r241: atalho pra tarefa de origem. Só aparece em lançamento
                gerado por apontamento de tarefa. */}
            {tarefaId && (
              <button onClick={abrirTarefaVinculada} disabled={carregandoTarefa}
                title={`Abrir a tarefa #${tarefaId}${lancamento?.tarefa_titulo ? ' — ' + lancamento.tarefa_titulo : ''}`}
                style={{
                  padding: '6px 12px', fontSize: '12px', fontWeight: '700',
                  backgroundColor: 'rgba(255,255,255,0.15)', color: COR.branco,
                  border: '1px solid rgba(255,255,255,0.5)', borderRadius: '5px',
                  cursor: carregandoTarefa ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  fontFamily: "'Manrope', sans-serif",
                }}>
                <IconeTicket tamanho={14} />
                Tarefa
              </button>
            )}
            <button onClick={() => onFechar(false)}
              style={{
                width: '28px', height: '28px', border: 'none',
                backgroundColor: 'transparent', fontSize: '22px',
                color: COR.branco, cursor: 'pointer', lineHeight: 1,
              }}>×</button>
          </div>
        </div>

        {/* Corpo: 2 colunas
            r56: alignItems flex-start + altura fixa nas duas colunas = altura do calendário,
            pra a justificativa do form acompanhar o fim do calendário
            r57 item 3: flex shrink em vez de flex grow — o modal cresce até
            o conteúdo natural, sem espaço vazio extra. */}
        <div style={{
          flexShrink: 1, minHeight: 0, overflowY: 'auto',
          padding: '16px 18px',
          display: 'flex', gap: '16px',
          alignItems: 'flex-start',
        }}>
          {/* Coluna 1: form */}
          <div style={{
            flex: 1, minWidth: 0,
            height: `${CAL_ALTURA_TOTAL}px`,  // r56: igual à coluna do calendário
            display: 'flex', flexDirection: 'column',
          }}>

            {periodoFechado && (
              <div style={{
                padding: '10px 14px', marginBottom: '12px',
                backgroundColor: COR.vermBg, color: COR.vermelho,
                border: `1px solid ${COR.vermelho}33`, borderRadius: '6px',
                fontSize: '12px', fontWeight: '600',
                flexShrink: 0,
              }}>
                ⚠ Este período está FECHADO. Não é permitido lançar nesta data.
              </div>
            )}

            {/* Linha 1: Analista + Data + Início + Qtd + Interv + Desloc + Total */}
            {/* r51: Total mais largo, Desloc menor */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2.2fr 1.1fr 0.85fr 0.85fr 0.85fr 0.85fr 0.95fr',
              gap: '8px', marginBottom: '12px',
              flexShrink: 0,
            }}>
              <div>
                <label style={labelStyle}>Analista</label>
                <Combobox
                  label={form.colaborador_label}
                  placeholder="Digite o nome..."
                  buscar={buscarColab}
                  disabled={!ehAdmin || periodoFechado}
                  onSelecionar={(op) => {
                    if (!op) return setForm(f => ({ ...f, colaborador_id: '', colaborador_label: '' }));
                    setForm(f => ({ ...f, colaborador_id: op.value, colaborador_label: op.label }));
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Data</label>
                {/* r209: com a data em branco, quem preenche a hora antes
                    da data nunca veria a tela de Duração — por isso ela também
                    é oferecida ao sair da data, se o início já estiver posto. */}
                <input type="date" value={form.data}
                       onChange={e => setCampo('data', e.target.value)}
                       onBlur={() => aoSairDoInicio(form.hora_inicio)}
                       disabled={periodoFechado}
                       style={{ ...inputStyle, ...(periodoFechado ? disabledStyle : {}) }} />
              </div>
              <div>
                <label style={labelStyle}>Início</label>
                {/* r207: mesma regra do apontamento de tarefas — de 15 em 15
                    minutos, encaixando no blur o que for digitado na mão.
                    Ao mudar o início, abre a tela de Definir Duração. */}
                <input type="time" value={form.hora_inicio}
                       onChange={e => setCampo('hora_inicio', e.target.value)}
                       onBlur={e => {
                         // r208: fora da grade, pergunta; dentro, segue o fluxo
                         // normal e oferece a tela de Duração.
                         const opcoes = opcoesDeHora(e.target.value);
                         if (opcoes.length) {
                           setAjuste({ campo: 'hora_inicio', valor: e.target.value, opcoes });
                           return;
                         }
                         aoSairDoInicio(e.target.value);
                       }}
                       step={PASSO_MINUTOS * 60}
                       disabled={periodoFechado}
                       style={{ ...inputStyle, ...(periodoFechado ? disabledStyle : {}) }} />
              </div>
              <div>
                <label style={labelStyle}>Qtd. horas</label>
                <CampoHHMM valor={form.qtd_horas} max={24}
                           onChange={v => setCampo('qtd_horas', v)}
                           aoDigitarForaDaGrade={(valor, opcoes) =>
                             setAjuste({ campo: 'qtd_horas', valor, opcoes })}
                           disabled={periodoFechado} />
              </div>
              <div>
                <label style={labelStyle}>Intervalos</label>
                <CampoHHMM valor={form.intervalo} readOnly disabled />
              </div>
              <div>
                <label style={labelStyle}>Desloc.</label>
                <CampoHHMM valor={form.deslocamento} readOnly disabled />
              </div>
              <div>
                <label style={labelStyle}>Total</label>
                <CampoHHMM valor={total} readOnly disabled
                           style={{ fontWeight: '700' }} />
              </div>
            </div>

            {/* r57 item 6: Linha 2 só Cliente + Projeto */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '12px', flexShrink: 0 }}>
              <div>
                <label style={labelStyle}>
                  Cliente <span style={{ color: COR.vermelho }}>*</span>
                </label>
                <Combobox
                  label={form.cliente_label}
                  placeholder="Nome do cliente..."
                  buscar={buscarClientes}
                  disabled={periodoFechado}
                  onSelecionar={aoSelecionarCliente}
                  renderItem={(op) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TagCliente tag={op.raw?.tag} cor={op.raw?.cor_tag} />
                      <span>{op.label}</span>
                    </div>
                  )}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Projeto <span style={{ color: COR.vermelho }}>*</span>
                </label>
                <Combobox
                  label={form.projeto_label}
                  placeholder={form.cliente_id ? 'Código ou descrição...' : 'Busca em todos os projetos...'}
                  buscar={buscarProjetos}
                  disabled={periodoFechado}
                  onSelecionar={aoSelecionarProjeto}
                />
              </div>
            </div>

            {/* r57 item 6: Linha 3 com Tipo + Chamado + Status
                r242: + Tarefa (origem do lançamento), logo ao lado do Chamado */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.7fr 1fr', gap: '10px', marginBottom: '12px', flexShrink: 0 }}>
              <div>
                <label style={labelStyle}>Tipo</label>
                <input type="text" value={TIPO_LANC_LABEL[form.tipo] || '—'}
                       readOnly disabled
                       style={{ ...inputStyle, ...disabledStyle }} />
              </div>
              <div>
                <label style={labelStyle}>Chamado</label>
                <input type="text" value={form.chamado}
                       onChange={e => {
                         // r55: auto-prepend # se houver conteúdo e não começar com #
                         let v = e.target.value;
                         if (v && !v.startsWith('#')) v = '#' + v;
                         setCampo('chamado', v);
                       }}
                       disabled={periodoFechado}
                       placeholder="#12345"
                       style={{ ...inputStyle, ...(periodoFechado ? disabledStyle : {}) }} />
              </div>
              {/* r242: tarefa de origem. Sempre somente leitura — quem manda no
                  vínculo é o apontamento, não a tela de horas. */}
              <div>
                <label style={labelStyle}>Tarefa</label>
                <input type="text" value={tarefaId || '—'}
                       readOnly disabled
                       title={tarefaId
                         ? `Tarefa #${tarefaId}${lancamento?.tarefa_titulo ? ' — ' + lancamento.tarefa_titulo : ''}`
                         : 'Lançamento sem origem em tarefa'}
                       style={{ ...inputStyle, ...disabledStyle }} />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                {(() => {
                  // r55: lógica de habilitação + opções
                  //   - Admin: 3 opções (Em Análise, Aprovado, Retrabalho), pode escolher
                  //   - Não-admin + tipo Projeto: 2 opções (Em Análise, Retrabalho), default Em Análise
                  //   - Não-admin + outro tipo: 3 opções, escolhe livremente
                  const restritoProjetoNaoAdmin = !ehAdmin && form.tipo === 'P';
                  const disabled = periodoFechado;
                  return (
                    <select value={form.status}
                            onChange={e => setCampo('status', e.target.value)}
                            disabled={disabled}
                            style={{
                              ...inputStyle,
                              ...(disabled ? disabledStyle : {}),
                            }}>
                      <option value="E">Em Análise</option>
                      {!restritoProjetoNaoAdmin && (
                        <option value="A">Aprovado</option>
                      )}
                      <option value="R">Retrabalho</option>
                    </select>
                  );
                })()}
              </div>
            </div>

            {/* Descrição com toggle Texto/Lista — r56: minHeight menor pra
                acomodar a justificativa no fim do calendário sem overflow */}
            <div style={{ flex: 1, minHeight: '60px', display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
              <DescricaoChecklist
                valor={form.descricao}
                onChange={(v) => setCampo('descricao', v)}
                disabled={periodoFechado}
              />
            </div>

            {/* Justificativa (sempre readonly, fixa pequena) */}
            <div style={{ flexShrink: 0 }}>
              <label style={labelStyle}>Justificativa</label>
              <textarea value={form.justificativa || ''}
                readOnly disabled
                placeholder="Preenchida apenas quando necessário (ex: rejeição, retrabalho)"
                style={{
                  width: '100%', height: '50px',
                  padding: '6px 10px', resize: 'none',
                  border: `1px solid ${COR.borda}`, borderRadius: '5px',
                  fontSize: '12px', fontFamily: "'Manrope', sans-serif",
                  outline: 'none', boxSizing: 'border-box',
                  ...disabledStyle,
                }} />
            </div>
          </div>

          {/* Coluna 2: Calendário lateral */}
          <div style={{ width: '360px', flexShrink: 0,
                        display: 'flex', flexDirection: 'column' }}>
            <CalendarioLateral
              colaboradorId={form.colaborador_id}
              data={form.data}
              ignorarId={lancamento?.id}
              meuLancamento={{
                hora_inicio: form.hora_inicio,
                qtd_horas: parseFloat(form.qtd_horas) || 0,
                intervalos: intervalosLocais,
                cliente_label: form.cliente_label,
                projeto_id: form.projeto_id,
                // r57 item 5: extrai a descrição do projeto a partir do label
                // ("NEG-123 — Descrição" → "Descrição")
                projeto_descricao: (() => {
                  const idx = (form.projeto_label || '').indexOf(' — ');
                  return idx > -1 ? form.projeto_label.slice(idx + 3) : '';
                })(),
                chamado: form.chamado,
              }}
              onAbrirIntervaloModal={(horaClickDec, horaStr) => {
                setModalIntervalo({ horaClickDec, horaStr });
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          {erro ? (
            <span style={{ color: COR.vermelho, fontSize: '12px', fontWeight: '600' }}>⚠ {erro}</span>
          ) : <span/>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => onFechar(false)}
              style={{
                padding: '8px 16px', fontSize: '12px', fontWeight: '600',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                backgroundColor: COR.branco, color: COR.muted, cursor: 'pointer',
              }}>Cancelar</button>
            <SePode rotina="lancamentos-horas" acao={ehNovo ? 'incluir' : 'alterar'}>
            <button onClick={salvar} disabled={salvando || periodoFechado}
              style={{
                padding: '8px 20px', fontSize: '12px', fontWeight: '700',
                border: 'none', borderRadius: '5px',
                backgroundColor: COR.azul, color: COR.branco,
                cursor: (salvando || periodoFechado) ? 'default' : 'pointer',
                opacity: (salvando || periodoFechado) ? 0.6 : 1,
              }}>{salvando ? 'Salvando...' : 'Salvar'}</button>
            </SePode>
          </div>
        </div>
      </div>

      {/* Modal de intervalo */}
      {/* r208: escolha entre as duas opções válidas do que foi digitado */}
      {ajuste && (
        <AjusteMultiploModal
          valorDigitado={ajuste.valor}
          opcoes={ajuste.opcoes}
          onEscolher={(v) => {
            setCampo(ajuste.campo, v);
            const eraInicio = ajuste.campo === 'hora_inicio';
            setAjuste(null);
            // Ajustado o início, segue pro fluxo normal (tela de Duração).
            if (eraInicio) aoSairDoInicio(v);
          }}
          onCancelar={() => setAjuste(null)}
        />
      )}

      {/* r207: Definir Duração — mesma tela do apontamento de tarefas */}
      {duracaoAberta && (
        <DuracaoModal
          inicioStr={`${form.data}T${form.hora_inicio}`}
          onAplicar={aplicarDuracao}
          onFechar={() => setDuracaoAberta(false)}
        />
      )}

      {modalIntervalo && (
        <IntervaloModal
          horaInicial={modalIntervalo.horaStr}
          onFechar={aoConfirmarIntervalo}
        />
      )}

      {/* r241: tarefa vinculada. O TarefaModal é 'position: absolute; inset: 0',
          então precisa de um pai posicionado que cubra a viewport — e com
          z-index acima do 1000 deste modal.
          r242: bloquearEdicao — daqui a tarefa é só consulta. Sem o atalho
          "Editar", então o modo nunca sai de 'visualizar'. */}
      {tarefaVinculada && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100 }}>
          <TarefaModal
            aberto
            modo="visualizar"
            bloquearEdicao
            tarefa={tarefaVinculada}
            onFechar={() => setTarefaVinculada(null)}
            onSalvar={() => setTarefaVinculada(null)}
            onAtualizar={recarregarTarefaVinculada}
          />
        </div>
      )}
    </div>
  );
}

export default LancamentoModal;
